import { createSlice } from "@reduxjs/toolkit";
import { sendChatMessage, getLocationFromCoordinates } from "../api/discoveryAgent";

interface Message {
    text: string;
    sender: "user" | "bot";
    loading?: boolean;
    isContextMessage?: boolean;
    timestamp?: number;
}

interface Location {
    latitude: number;
    longitude: number;
    city?: string;
    state?: string;
    country?: string;
    formattedAddress?: string;
}

interface DiscoveryAgentState {
    loading: boolean;
    currentRequestId: undefined | string;
    messages: Message[];
    location: Location | null;
    locationError: string | null;
    chatError: string | null;
    showLocationNotification: boolean;
}

const initialState: DiscoveryAgentState = {
    loading: false,
    currentRequestId: undefined,
    messages: [],
    location: null,
    locationError: null,
    chatError: null,
    showLocationNotification: false,
};

export const discoveryAgentSlice = createSlice({
    name: 'discoveryAgent',
    initialState,
    reducers: {
        resetDiscoveryAgent: () => initialState,

        addUserMessage: (state, action) => {
            const message: Message = {
                text: action.payload.text,
                sender: "user",
                timestamp: Date.now(),
            };
            state.messages.push(message);
        },

        addBotMessage: (state, action) => {
            const message: Message = {
                text: action.payload.text,
                sender: "bot",
                timestamp: Date.now(),
                isContextMessage: action.payload.isContextMessage || false,
            };
            state.messages.push(message);
        },

        addLoadingMessage: (state) => {
            const message: Message = {
                text: "...",
                sender: "bot",
                loading: true,
                timestamp: Date.now(),
            };
            state.messages.push(message);
        },

        removeLoadingMessage: (state) => {
            state.messages = state.messages.filter(msg => !(msg.sender === "bot" && msg.loading));
        },

        setLocation: (state, action) => {
            state.location = {
                ...state.location,
                ...action.payload,
            };
        },

        setLocationError: (state, action) => {
            state.locationError = action.payload;
        },

        setShowLocationNotification: (state, action) => {
            state.showLocationNotification = action.payload;
        },

        clearMessages: (state) => {
            state.messages = [];
        },

        clearErrors: (state) => {
            state.chatError = null;
            state.locationError = null;
        },
    },
    extraReducers(builder) {
        builder
            .addCase("RESET_ALL", (state, action) => {
                return initialState;
            })

            // Send Chat Message
            .addCase(sendChatMessage.pending, (state, action) => {
                state.loading = true;
                state.currentRequestId = action.meta.requestId;
                state.chatError = null;
            })
            .addCase(sendChatMessage.fulfilled, (state, action) => {
                const { requestId } = action.meta;
                if (state.loading && state.currentRequestId === requestId) {
                    state.loading = false;
                    state.currentRequestId = undefined;

                    // Remove loading message
                    state.messages = state.messages.filter(msg => !(msg.sender === "bot" && msg.loading));

                    // Add bot response
                    if (action.payload.data) {
                        const botMessage: Message = {
                            text: action.payload.data,
                            sender: "bot",
                            timestamp: Date.now(),
                        };
                        state.messages.push(botMessage);
                    }
                }
            })
            .addCase(sendChatMessage.rejected, (state, action) => {
                const { requestId } = action.meta;
                if (state.loading && state.currentRequestId === requestId) {
                    state.loading = false;
                    state.currentRequestId = undefined;

                    // Remove loading message
                    state.messages = state.messages.filter(msg => !(msg.sender === "bot" && msg.loading));

                    // Add error message
                    const errorMessage: Message = {
                        text: "Sorry, there was an error processing your request. Please try again.",
                        sender: "bot",
                        timestamp: Date.now(),
                    };
                    state.messages.push(errorMessage);

                    state.chatError = action.error.message || "Failed to send message";
                }
            })

            // Get Location from Coordinates
            .addCase(getLocationFromCoordinates.pending, (state, action) => {
                // Don't set loading state for location requests as they're background
            })
            .addCase(getLocationFromCoordinates.fulfilled, (state, action) => {
                // Check if the payload has data (success case) and no error
                if ('data' in action.payload && action.payload.data && state.location) {
                    state.location = {
                        ...state.location,
                        city: action.payload.data.city,
                        state: action.payload.data.state,
                        country: action.payload.data.country,
                        formattedAddress: action.payload.data.formattedAddress,
                    };
                    // Clear any previous location errors on success
                    state.locationError = null;
                } else if ('error' in action.payload) {
                    // Handle error case
                    state.locationError = action.payload.error || null;
                }
            })
            .addCase(getLocationFromCoordinates.rejected, (state, action) => {
                state.locationError = action.error.message || "Failed to get location details";
            });
    }
});

export const {
    resetDiscoveryAgent,
    addUserMessage,
    addBotMessage,
    addLoadingMessage,
    removeLoadingMessage,
    setLocation,
    setLocationError,
    setShowLocationNotification,
    clearMessages,
    clearErrors,
} = discoveryAgentSlice.actions;

export default discoveryAgentSlice.reducer; 